import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { documentsApi, DocumentStatus, TemplateKind } from "@/modules/document/public";
import { templatesApi, TemplateStatus, TemplateSupport } from "@/modules/templating/public";
// R24 caps a page at 100: both catalogues are read page by page until the last one, so every
// template is selectable however many a tenant has.
const PAGE_SIZE = 100;
async function loadAllPages(fetchPage) {
    const items = [];
    for (let page = 1;; page += 1) {
        const result = await fetchPage(page);
        items.push(...result.items);
        if (page >= result.totalPages)
            return items;
    }
}
function toOptions(items) {
    return items
        .map((item) => ({ value: item.id, label: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}
/**
 * The two template catalogues the tax-receipt settings page picks from: OPDOC document templates
 * (the receipt PDF, per donor type) and OPINK published email templates (default and rules of the
 * two email contexts). Each catalogue loads on its own, so one failing leaves the other usable.
 */
export function useTaxReceiptTemplateOptions() {
    const { t } = useI18n();
    const T = "finance.taxReceipts.settings";
    const documentTemplates = ref([]);
    const emailTemplates = ref([]);
    const documentsLoadError = ref(false);
    const emailTemplatesLoadError = ref(false);
    async function loadDocumentTemplates() {
        documentsLoadError.value = false;
        try {
            // OPDON renders a receipt only from a Standardized (locked PDF) template and refuses an
            // Editable one at generation time, so the picker never offers one (R42).
            documentTemplates.value = toOptions(await loadAllPages((page) => documentsApi.getDocuments({
                statuses: [DocumentStatus.Active],
                templateKind: TemplateKind.Standardized,
                page,
                pageSize: PAGE_SIZE,
                sortBy: "name",
                sortDirection: "asc",
            })));
        }
        catch (err) {
            logger.error("Failed to load the document templates for the tax-receipt settings", err);
            documentsLoadError.value = true;
        }
    }
    async function loadEmailTemplates() {
        emailTemplatesLoadError.value = false;
        try {
            emailTemplates.value = toOptions(await loadAllPages((page) => templatesApi.getTemplates({
                support: TemplateSupport.Email,
                status: TemplateStatus.Published,
                page,
                pageSize: PAGE_SIZE,
            })));
        }
        catch (err) {
            logger.error("Failed to load the email templates for the tax-receipt settings", err);
            emailTemplatesLoadError.value = true;
        }
    }
    async function load() {
        await Promise.all([loadDocumentTemplates(), loadEmailTemplates()]);
    }
    /**
     * The options plus a placeholder entry for every selected id the catalogue does not list (a
     * template archived or unpublished since), so the select keeps showing a value.
     */
    function withSelected(options, selectedIds) {
        const listed = new Set(options.map((option) => option.value));
        const missing = [...new Set(selectedIds.filter((id) => !!id && !listed.has(id)))];
        return [...options, ...missing.map((id) => ({ value: id, label: t(`${T}.unknownTemplate`) }))];
    }
    return {
        documentTemplates: computed(() => documentTemplates.value),
        emailTemplates: computed(() => emailTemplates.value),
        documentsLoadError,
        emailTemplatesLoadError,
        load,
        withSelected,
    };
}
